import {useCallback, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {ColumnDef} from '@tanstack/react-table';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import {PageTitle} from '../../../_metronic/layout/core';
import TableMetronic from '../../../app/common/components/table/TableMetronic';
import CustomModal from '../../../app/common/components/modal/CustomModal';

interface DnsEntry {
  id: number;
  type: string;
  hote: string;
  donnees: string;
  priorite: string;
  ttl: number;
}

interface DnsFormValues {
  type: string;
  hote: string;
  donnees: string;
  ttl: string;
}

const DNS_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA'];

const MOCK_ENTRIES: DnsEntry[] = [
  {id: 1, type: 'A', hote: 'emlsrv.fr.', donnees: '81.93.243.10', priorite: '—', ttl: 3600},
];

const EMPTY_FORM: DnsFormValues = {type: '', hote: '', donnees: '', ttl: '3600'};

const DomaineZoneDnsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const domaineName = searchParams.get('domaine') ?? 'exemple.fr';
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<DnsEntry | null>(null);
  const [form, setForm] = useState<DnsFormValues>(EMPTY_FORM);
  const [entryToDelete, setEntryToDelete] = useState<DnsEntry | null>(null);

  const handleChange =
    (field: keyof DnsFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(prev => ({...prev, [field]: e.target.value}));

  const handleClose = () => {
    setShowModal(false);
    setEditingEntry(null);
    setForm(EMPTY_FORM);
  };

  const openEdit = useCallback((entry: DnsEntry) => {
    setEditingEntry(entry);
    setForm({
      type: entry.type,
      hote: entry.hote.replace(`.${domaineName}`, '').replace(/\.$/, ''),
      donnees: entry.donnees,
      ttl: String(entry.ttl),
    });
    setShowModal(true);
  }, [domaineName]);

  const columns = useMemo<ColumnDef<DnsEntry>[]>(
    () => [
      {header: "Type d'enregistrement", accessorKey: 'type'},
      {
        header: 'Hôte',
        accessorKey: 'hote',
        cell: info => <span className="text-primary">{info.getValue() as string}</span>,
      },
      {header: 'Données', accessorKey: 'donnees'},
      {header: 'Priorité', accessorKey: 'priorite'},
      {header: 'TTL', accessorKey: 'ttl', size: 80},
      {
        header: 'Action',
        accessorKey: 'action',
        size: 100,
        enableSorting: false,
        meta: {noClick: true},
        cell: info => (
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-icon btn-primary"
              title="Modifier"
              onClick={() => openEdit(info.row.original)}
            >
              <i className="bi bi-pencil-square" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-icon btn-danger"
              title="Supprimer"
              onClick={() => setEntryToDelete(info.row.original)}
            >
              <i className="bi bi-trash" />
            </button>
          </div>
        ),
      },
    ],
    [openEdit, setEntryToDelete],
  );

  const modalBody = (
    <div className="d-flex flex-column gap-6">
      <Form.Group>
        <Form.Label className="fw-bold fs-6 mb-2">Type d'enregistrement</Form.Label>
        <Form.Select value={form.type} onChange={handleChange('type')}>
          <option value="" />
          {DNS_TYPES.map(t => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group>
        <Form.Label className="fw-bold fs-6 mb-2">Hôte</Form.Label>
        <InputGroup>
          <Form.Control
            type="text"
            value={form.hote}
            onChange={handleChange('hote')}
            placeholder=""
          />
          <InputGroup.Text className="text-muted">.{domaineName}</InputGroup.Text>
        </InputGroup>
      </Form.Group>

      <Form.Group>
        <Form.Label className="fw-bold fs-6 mb-2">Données</Form.Label>
        <Form.Control
          type="text"
          value={form.donnees}
          onChange={handleChange('donnees')}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label className="fw-bold fs-6 mb-2">TTL</Form.Label>
        <Form.Control
          type="number"
          value={form.ttl}
          onChange={handleChange('ttl')}
        />
      </Form.Group>
    </div>
  );

  const modalFooter = (
    <>
      <Button variant="secondary" size="sm" onClick={handleClose}>
        Fermer
      </Button>
      <Button variant="primary" size="sm">
        <i className="bi bi-plus-lg me-1" />
        Ajouter
      </Button>
    </>
  );

  return (
    <>
      <PageTitle breadcrumbs={[]}>Zone DNS — {domaineName}</PageTitle>

      <div className="d-flex justify-content-between align-items-center mb-5">
        <button
          type="button"
          className="btn btn-sm btn-outline btn-outline-primary btn-active-light-primary d-inline-flex align-items-center"
          onClick={() => navigate(-1)}
        >
          <i className="bi bi-arrow-left me-2" />
          Retour
        </button>

        <button
          type="button"
          className="btn btn-sm btn-primary d-inline-flex align-items-center"
          onClick={() => setShowModal(true)}
        >
          <i className="bi bi-plus-lg me-2" />
          Ajouter une entrée DNS
        </button>
      </div>

      <TableMetronic<DnsEntry>
        title="Gérer la zone DNS"
        data={MOCK_ENTRIES}
        columns={columns}
        searchBar
        pagination
        elementName="entrée DNS"
        elementNamePlural="entrées DNS"
      />

      <CustomModal
        show={showModal}
        onHide={handleClose}
        title={editingEntry ? 'Modifier une entrée DNS' : 'Ajouter une entrée DNS'}
        body={modalBody}
        footer={modalFooter}
      />

      <CustomModal
        show={entryToDelete !== null}
        onHide={() => setEntryToDelete(null)}
        title="Confirmer la suppression"
        centered
        body={
          <div className="d-flex align-items-start gap-4">
            <i className="bi bi-exclamation-triangle-fill text-danger fs-2x flex-shrink-0 mt-1" />
            <div>
              <p className="fw-semibold mb-2">
                Êtes-vous sûr de vouloir supprimer cette entrée DNS ?
              </p>
              <p className="mb-2">
                Type&nbsp;: <strong>{entryToDelete?.type}</strong> — Hôte&nbsp;:{' '}
                <strong>{entryToDelete?.hote}</strong>
              </p>
              <p className="mb-0 text-danger">Cette action est irréversible.</p>
            </div>
          </div>
        }
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setEntryToDelete(null)}>
              Annuler
            </Button>
            <Button variant="danger" size="sm" onClick={() => setEntryToDelete(null)}>
              <i className="bi bi-trash me-1" />
              Supprimer
            </Button>
          </>
        }
      />
    </>
  );
};

export default DomaineZoneDnsPage;
